/**
 * Cart Location Selector
 * Maneja selectores de Región y Comuna en el cart drawer
 * Pre-rellena campos del checkout de Shopify vía URL
 */

// Mapeo de nombres de regiones: desde nuestro formato a códigos ISO 3166-2 de Chile
// Shopify usa códigos de región de dos letras (sin prefijo CL-)
const SHOPIFY_REGION_MAPPING = {
  "Arica y Parinacota": "AP",  // CL-AP
  "Tarapacá": "TA",  // CL-TA
  "Antofagasta": "AN",  // CL-AN
  "Atacama": "AT",  // CL-AT
  "Coquimbo": "CO",  // CL-CO
  "Valparaíso": "VS",  // CL-VS
  "Región Metropolitana": "RM",  // CL-RM (Santiago Metropolitan)
  "O'Higgins": "LI",  // CL-LI (Libertador General Bernardo O'Higgins)
  "Maule": "ML",  // CL-ML
  "Ñuble": "NB",  // CL-NB
  "Biobío": "BI",  // CL-BI
  "La Araucanía": "AR",  // CL-AR
  "Los Ríos": "LR",  // CL-LR
  "Los Lagos": "LL",  // CL-LL
  "Aysén": "AI",  // CL-AI
  "Magallanes": "MA"  // CL-MA
};

class CartLocationSelector extends HTMLElement {
  constructor() {
    super();
    this.regionSelect = this.querySelector('[data-region-select]');
    this.communeSelect = this.querySelector('[data-commune-select]');
    this.checkoutButtons = null;

    if (!this.regionSelect || !this.communeSelect) return;

    this.init();
  }

  init() {
    // Cargar regiones
    this.loadRegions();

    // Event listeners
    this.regionSelect.addEventListener('change', this.handleRegionChange.bind(this));
    this.communeSelect.addEventListener('change', this.handleCommuneChange.bind(this));

    // Interceptar submit del formulario del cart drawer
    this.setupFormInterception();

    // Validar al cargar
    this.validateAndUpdateCheckout();

    // Observar cambios en el drawer (cuando se abre/actualiza)
    this.observeDrawerUpdates();
  }

  setupFormInterception() {
    // Detectar si estamos en el drawer o en la página del carrito
    const formDrawer = document.getElementById('CartDrawer-Form');
    const formCart = document.getElementById('cart');
    const form = formDrawer || formCart;

    if (!form) return;

    form.addEventListener('submit', (e) => {
      // Solo interceptar si se está haciendo checkout (no update del carrito)
      const submitter = e.submitter;
      if (submitter && submitter.name === 'checkout') {
        const region = this.regionSelect.value;
        const commune = this.communeSelect.value;

        if (!region || !commune) {
          // Si no están seleccionados, prevenir submit y mostrar error
          e.preventDefault();
          this.showError();
          return false;
        }

        // Si están seleccionados, modificar la acción del formulario
        e.preventDefault();
        const checkoutUrl = this.buildCheckoutUrl(region, commune);

        // DEBUG: Log para verificar qué se está enviando
        console.log('=== CHECKOUT DEBUG ===');
        console.log('Región seleccionada:', region);
        console.log('Comuna seleccionada:', commune);
        console.log('Región mapeada para Shopify:', SHOPIFY_REGION_MAPPING[region] || region);
        console.log('URL del checkout:', checkoutUrl);
        console.log('======================');

        window.location.href = checkoutUrl;
        return false;
      }
    });
  }

  loadRegions() {
    if (typeof CHILE_REGIONS === 'undefined') {
      console.error('CHILE_REGIONS no está definido');
      return;
    }

    // Limpiar y agregar opción por defecto
    this.regionSelect.innerHTML = '<option value="">Selecciona tu región</option>';

    // Agregar todas las regiones
    Object.keys(CHILE_REGIONS).forEach(region => {
      const option = document.createElement('option');
      option.value = region;
      option.textContent = region;
      this.regionSelect.appendChild(option);
    });
  }

  handleRegionChange(event) {
    const selectedRegion = event.target.value;

    // Limpiar selector de comunas
    this.communeSelect.innerHTML = '<option value="">Selecciona tu comuna</option>';
    this.communeSelect.disabled = !selectedRegion;

    if (selectedRegion && CHILE_REGIONS[selectedRegion]) {
      // Cargar comunas de la región seleccionada
      CHILE_REGIONS[selectedRegion].forEach(commune => {
        const option = document.createElement('option');
        option.value = commune;
        option.textContent = commune;
        this.communeSelect.appendChild(option);
      });
    }

    // Guardar en sessionStorage
    if (selectedRegion) {
      sessionStorage.setItem('cart_selected_region', selectedRegion);
      sessionStorage.removeItem('cart_selected_commune');
    }

    this.validateAndUpdateCheckout();
  }

  handleCommuneChange(event) {
    const selectedCommune = event.target.value;

    // Guardar en sessionStorage
    if (selectedCommune) {
      sessionStorage.setItem('cart_selected_commune', selectedCommune);
    }

    this.validateAndUpdateCheckout();
  }

  validateAndUpdateCheckout() {
    const region = this.regionSelect.value;
    const commune = this.communeSelect.value;
    const isValid = region && commune;

    // Actualizar botones de checkout
    this.updateCheckoutButtons(isValid, region, commune);

    return isValid;
  }

  updateCheckoutButtons(isValid, region, commune) {
    // Ocultar mensaje de error si la selección es válida
    const errorMsg = this.querySelector('[data-location-error]');
    if (errorMsg && isValid) {
      errorMsg.style.display = 'none';
    }

    // Los botones siempre están habilitados, la validación se hace en el submit del form
    // No necesitamos hacer nada más aquí
  }

  buildCheckoutUrl(region, commune) {
    // Convertir el nombre de la región a código ISO 3166-2
    const shopifyRegion = SHOPIFY_REGION_MAPPING[region] || region;

    // Construir URL con parámetros para pre-rellenar el checkout
    // IMPORTANTE: Shopify requiere el país para poder mapear correctamente la provincia
    const params = new URLSearchParams();
    params.append('checkout[shipping_address][country]', 'Chile');  // País primero
    params.append('checkout[shipping_address][province]', shopifyRegion);  // Código ISO
    params.append('checkout[shipping_address][city]', commune);

    return `/checkout?${params.toString()}`;
  }

  showError() {
    // Expandir el acordeón si está colapsado
    const detailsElement = this.querySelector('#Details-CartLocation');
    if (detailsElement && !detailsElement.open) {
      detailsElement.open = true;
    }

    const errorMsg = this.querySelector('[data-location-error]');
    if (errorMsg) {
      errorMsg.style.display = 'block';

      // Hacer scroll al acordeón/error después de expandir
      setTimeout(() => {
        const accordion = this.closest('cart-location-selector');
        if (accordion) {
          accordion.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);

      // Animar el error
      errorMsg.classList.add('shake');
      setTimeout(() => {
        errorMsg.classList.remove('shake');
      }, 500);
    }

    // Focus en el primer campo vacío después de un pequeño delay
    setTimeout(() => {
      if (!this.regionSelect.value) {
        this.regionSelect.focus();
      } else if (!this.communeSelect.value) {
        this.communeSelect.focus();
      }
    }, 150);
  }

  observeDrawerUpdates() {
    // Restaurar valores guardados cuando se abre el drawer
    const savedRegion = sessionStorage.getItem('cart_selected_region');
    const savedCommune = sessionStorage.getItem('cart_selected_commune');

    if (savedRegion) {
      this.regionSelect.value = savedRegion;
      this.handleRegionChange({ target: this.regionSelect });

      if (savedCommune) {
        // Esperar a que las comunas se carguen
        setTimeout(() => {
          this.communeSelect.value = savedCommune;
          this.handleCommuneChange({ target: this.communeSelect });
        }, 100);
      }
    }

    // Escuchar evento de apertura del drawer
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartOpen, () => {
        this.validateAndUpdateCheckout();
      });
    }
  }

  // Método público para validar desde fuera
  validate() {
    return this.validateAndUpdateCheckout();
  }
}

// Registrar el custom element
if ('customElements' in window) {
  customElements.define('cart-location-selector', CartLocationSelector);
}
